import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Typography } from '@mui/material';
import VoiceMessageWaveform from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/VoiceMessageBubble/VoiceMessageWaveform';
import { arrayOf, number, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';
import LiveWaveformStrip from './LiveWaveformStrip';

/** WhatsApp-style M:SS */
function formatTime(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const PausedVoiceRecordingPreview = ({
  recordingTime,
  previewUrl = null,
  previewHeights,
  liveWaveformHeights,
}) => {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [metaDuration, setMetaDuration] = useState(0);
  const [previewElapsed, setPreviewElapsed] = useState(0);

  const hasAudio = Boolean(previewUrl);
  const hasBars = previewHeights.length > 0;

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !previewUrl) return;
    a.load();
    setProgress(0);
    setAudioPlaying(false);
    setMetaDuration(0);
    setPreviewElapsed(0);
  }, [previewUrl]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;

    const getDuration = () => {
      if (Number.isFinite(a.duration) && a.duration > 0) return a.duration;
      if (metaDuration > 0) return metaDuration;
      return Math.max(0.001, recordingTime);
    };

    const syncProgress = () => {
      const d = getDuration();
      setProgress(Math.max(0, Math.min(1, a.currentTime / d)));
      setPreviewElapsed(a.currentTime);
    };

    const onPlay = () => setAudioPlaying(true);
    const onPause = () => setAudioPlaying(false);
    const onEnded = () => {
      setAudioPlaying(false);
      setProgress(0);
      setPreviewElapsed(0);
      if (a) a.currentTime = 0;
    };
    const onTime = () => syncProgress();
    const onMeta = () => {
      if (Number.isFinite(a.duration) && a.duration > 0) {
        setMetaDuration(a.duration);
      }
      syncProgress();
    };

    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);

    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
    };
  }, [previewUrl, metaDuration, recordingTime]);

  const togglePreviewPlay = useCallback(() => {
    const a = audioRef.current;
    if (!a || !hasAudio) return;
    if (a.paused) {
      void a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [hasAudio]);

  const seekToProgress = useCallback(
    p => {
      const a = audioRef.current;
      if (!a || !hasAudio) return;
      let d = Number.isFinite(a.duration) && a.duration > 0 ? a.duration : metaDuration;
      if (!d || d <= 0) d = Math.max(0.001, recordingTime);
      a.currentTime = Math.max(0, Math.min(1, p)) * d;
      setProgress(Math.max(0, Math.min(1, p)));
      setPreviewElapsed(a.currentTime);
    },
    [hasAudio, metaDuration, recordingTime],
  );

  const handleScrubStart = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const handleScrubEnd = useCallback(() => {
    /* seek applied in onSeekProgress */
  }, []);

  const displaySeconds =
    hasAudio && (audioPlaying || progress > 0.001) ? previewElapsed : recordingTime;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- voice recording preview, no captions */}
      {hasAudio ? <audio ref={audioRef} src={previewUrl} preload="auto" /> : null}

      <IconButton
        type="button"
        size="small"
        onClick={togglePreviewPlay}
        disabled={!hasAudio}
        aria-label={
          audioPlaying
            ? I18n.t('SendMessageForm.Pause recording preview')
            : I18n.t('SendMessageForm.Play recording preview')
        }
        sx={theme => ({
          color: theme.palette.text.secondary,
          padding: theme.spacing(0.75),
          flexShrink: 0,
        })}
      >
        {audioPlaying ? (
          <PauseIcon sx={{ fontSize: 26 }} />
        ) : (
          <PlayArrowIcon sx={{ fontSize: 26 }} />
        )}
      </IconButton>

      <Typography
        component="span"
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 13,
          color: 'text.secondary',
          minWidth: 36,
          flexShrink: 0,
        }}
      >
        {formatTime(displaySeconds)}
      </Typography>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {hasBars && hasAudio ? (
          <VoiceMessageWaveform
            variant="bars"
            heights={previewHeights}
            progress={progress}
            onSeekProgress={seekToProgress}
            onScrubStart={handleScrubStart}
            onScrubEnd={handleScrubEnd}
          />
        ) : (
          <LiveWaveformStrip heights={hasBars ? previewHeights : liveWaveformHeights} />
        )}
      </Box>
    </>
  );
};

PausedVoiceRecordingPreview.propTypes = {
  recordingTime: number.isRequired,
  previewUrl: string,
  previewHeights: arrayOf(number).isRequired,
  liveWaveformHeights: arrayOf(number).isRequired,
};

export default PausedVoiceRecordingPreview;
